import React, { useEffect, useRef, useState } from "react";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { FaLocationDot } from "react-icons/fa6";
import { FaEdit, FaTrash } from "react-icons/fa";
import ApiService from "../../services/ApiService";
import Paginator from "../Paginator";
import { toast } from "react-toastify";
import { Button, Col, Row } from "react-bootstrap";
import { ChevronRight, ChevronLeft } from "lucide-react";
import NoDataAnimation from "../../assets/animations/No Result Green theme.json"
import Lottie from "lottie-react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

const MyJob = () => {
  const [jobData, setJobData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [pagination, setPagination] = useState({
    total_pages: 1,
    current_page: 1,
  });
const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [confirmDeleteJob, setConfirmDeleteJob] = useState(null);
  const [editJobData, setEditJobData] = useState(null);
  const [createJobData, setCreateJobData] = useState(false);
  const [editForm, setEditForm] = useState({
    title: "",
    company: "",
    location: "",
  });
  const [createForm, setCreateForm] = useState({
    title: "",
    company: "",
    location: "",
  });

  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchJobData(1);
  }, []);

  useEffect(() => {
    document.body.style.overflow =
      confirmDeleteJob || editJobData ? "hidden" : "auto";
  }, [confirmDeleteJob, editJobData]);

  const fetchJobData = async (page = 1) => {
    try {
      setIsLoading(true);
      const res = await ApiService.request({
        method: "GET",
        url: `jobs/list?page=${page}`,
      });

      const data = res.data;
      if (data.status) {
        setJobData(data.data.jobs);
        setPagination(data.data.pagination);
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      console.error("Fetch Job Error:", err);
      toast.error("Failed to fetch jobs.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (
      page !== pagination.current_page &&
      page >= 1 &&
      page <= pagination.total_pages
    ) {
      fetchJobData(page);
    }
  };

  const handleDeleteJob = async () => {
    try {
      setIsLoading(true);
      const res = await ApiService.request({
        method: "DELETE",
        url: `jobs/${confirmDeleteJob.id}`,
      });

      if (res.data.status) {
        setConfirmDeleteJob(null);
        setOpenMenuId(null);
        await fetchJobData(1);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      console.error("Delete Error:", err);
      toast.error("Failed to delete job.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateJob = async () => {
    try {
      setIsLoading(true);
      const res = await ApiService.request({
        method: "POST",
        url: `jobs/createOrUpdate`,
        data: createForm,
      });

      if (res.data.status) {
        setCreateJobData(false);
        await fetchJobData(1);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      console.error("Edit Error:", err);
      toast.error("Failed to update job.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditJob = async () => {
    try {
      setIsLoading(true);
      const res = await ApiService.request({
        method: "POST",
        url: `jobs/createOrUpdate/${editJobData._id}`,
        data: editForm,
      });

      if (res.data.status) {
        setEditJobData(null);
        setOpenMenuId(null);
        await fetchJobData(1);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      console.error("Edit Error:", err);
      toast.error("Failed to update job.");
    } finally {
      setIsLoading(false);
    }
  };


const renderJobCard = (job) => {
  const isOpen = openMenuId === job._id;

  const handleToggleMenu = (e) => {
    // Get position of the icon
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + window.scrollY,
      left: rect.right - 140, 
    });
    setOpenMenuId(isOpen ? null : job._id);
  };

  const menu = (
<div
  className="bg-white shadow rounded p-2"
  style={{
    position: "absolute",
    top: `${menuPosition.top}px`,
    left: `${menuPosition.left}px`,
    width: "140px",
    zIndex: 9999,
    direction: isRTL ? "rtl" : "ltr",
  }}
>
  <div
    className="d-flex align-items-center mb-2 cursor-pointer"
    onClick={() => {
      setEditJobData(job);
      setEditForm({
        title: job.title,
        company: job.company,
        location: job.location,
      });
      setOpenMenuId(null);
    }}
    style={{ 
      flexDirection: isRTL ? "row-reverse" : "row",
      justifyContent: "space-between",
      gap: "8px"
    }}
  >
    <FaEdit style={{ color: "#e15d4f", flexShrink: 0 }} />
    <span style={{ 
      textAlign: isRTL ? "right" : "left",
      flex: 1
    }}>{t("edit")}</span>
  </div>
  <div
    className="d-flex align-items-center cursor-pointer"
    onClick={() =>
      setConfirmDeleteJob({ id: job._id, title: job.title })
    }
    style={{ 
      flexDirection: isRTL ? "row-reverse" : "row",
      justifyContent: "space-between",
      gap: "8px"
    }}
  >
    <FaTrash style={{ color: "#e15d4f", flexShrink: 0 }} />
    <span style={{ 
      textAlign: isRTL ? "right" : "left",
      flex: 1
    }}>{t("delete")}</span>
  </div>
</div>
  );

  return (
     <div
      key={job._id}
      className="d-flex justify-content-between align-items-center my-3 border-bottom pb-3"
      style={{ direction: isRTL ? "rtl" : "ltr" }}
    >
      <div className="d-flex align-items-start" style={{ 
        flexDirection: isRTL ? "row-reverse" : "row" 
      }}>
        <div className="mt-1" style={{ [isRTL ? "marginRight" : "marginLeft"]: "12px" }}>
          <h5 className="mb-1" style={{ 
            fontSize: "17px", 
            textAlign: isRTL ? "right" : "left" 
          }}>
            {job.title}
          </h5>
          <p className="m-0 mb-1" style={{ 
            fontSize: "16px", 
            textAlign: isRTL ? "right" : "left" 
          }}>
            {job.company}
          </p>
          <p
            className="m-0 text-muted"
            style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "6px",
              flexDirection: isRTL ? "row-reverse" : "row",
              justifyContent: isRTL ? "flex-end" : "flex-start"
            }}
          >
            <FaLocationDot className={isRTL ? "ms-1" : "me-1"} style={{ color: "#e15d4f" }} />
            <span style={{ textAlign: isRTL ? "right" : "left" }}>
              {[job.location].filter(Boolean).join(", ")}
            </span>
          </p>
        </div>
      </div>

      <div className="position-relative">
        <HiOutlineDotsVertical
          fontSize={23}
          className="cursor-pointer"
          onClick={handleToggleMenu}
          style={{ color: "#e15d4f" }}
        />
      </div>

      {/* Render menu outside scroll using portal */}
      {isOpen && ReactDOM.createPortal(menu, document.body)}
    </div>
  );
};



  return (
    <div className="inter-font container">
      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
       <div className="inter-font spinner-border" role="status"></div>
      </div>
      ) : (
        <>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "end",
              alignItems: "end",
            }}
          >
            <button
              className="inter-font btn inter-font"
              style={{ backgroundColor: "#e15d4f", color: "#fff" }}
              onClick={() => setCreateJobData(true)}
            >
              {t("t_my_Profile_tab2_text3")}
            </button>
          </div>
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {jobData.length === 0 ? (
            <div className="d-flex flex-column align-items-center py-5">
            <div style={{ width: 200 }}>
              <Lottie animationData={NoDataAnimation} loop={true} />
            </div>
            </div>
            ) : (
              jobData.map(renderJobCard)
            )}
          </div>

          
            {pagination?.total_pages > 1 && (
                <Paginator
                    page={pagination.current_page}
                    rows={pagination.per_page}
                    totalRecords={pagination.total_records}
                    onPageChange={handlePageChange}
                />
            )}

       
        </>
      )}

{/* Delete Confirmation Modal */}
{confirmDeleteJob && (
  <div
    className="inter-font modal fade show d-block"
    tabIndex="-1"
    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    dir={isRTL ? "rtl" : "ltr"}
  >
    <div className="inter-font modal-dialog modal-dialog-centered">
      <div
        className="inter-font modal-content border-0 shadow-lg rounded-4"
        style={{ backgroundColor: "#fff6f1" }}
      >
        <div className="inter-font modal-header border-0" style={{ direction: isRTL ? "rtl" : "ltr" }}>
          <h5 className="inter-font modal-title fw-bold text-danger" style={{ textAlign: isRTL ? "right" : "left", width: "100%" }}>
            {t("delete_confirmation")}
          </h5>
          <button
            type="button"
            className="inter-font btn-close"
            onClick={() => {
              setConfirmDeleteJob(null);
              setOpenMenuId(null);
            }}
            style={isRTL ? { marginLeft: "0", marginRight: "auto", order: -1 } : {}}
          ></button>
        </div>
        <div className="inter-font modal-body" style={{ direction: isRTL ? "rtl" : "ltr" }}>
          <p className="inter-font mb-0 text-dark" style={{ textAlign: isRTL ? "right" : "left" }}>
            {t("delete_job_confirmation")}: <strong>{confirmDeleteJob.title}</strong>
          </p>
        </div>
        <div className="inter-font modal-footer border-0" style={{ direction: isRTL ? "rtl" : "ltr" }}>
          <button
            className="inter-font btn btn-light"
            onClick={() => {
              setConfirmDeleteJob(null);
              setOpenMenuId(null);
            }}
          >
            {t("new_text_30")} 
          </button>
          <button
            className="inter-font btn"
            style={{ backgroundColor: "#e15d4f", color: "#fff" }}
            onClick={handleDeleteJob}
          >
            {t("delete")}
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{/* Create Job Modal */}
{createJobData && (
  <div
    className="inter-font modal fade show d-block"
    tabIndex="-1"
    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    dir={isRTL ? "rtl" : "ltr"}
  >
    <div className="inter-font modal-dialog modal-dialog-centered">
      <div
        className="inter-font modal-content border-0 shadow-lg rounded-4"
        style={{ backgroundColor: "#fff6f1" }}
      >
        <div className="inter-font modal-header border-0" style={{ direction: isRTL ? "rtl" : "ltr" }}>
          <h5 className="inter-font modal-title fw-bold" style={{ textAlign: isRTL ? "right" : "left", width: "100%" }}>
            {t("t_my_Profile_tab2_text3")} {/* Create Job */}
          </h5>
          <button
            type="button"
            className="inter-font btn-close"
            onClick={() => {
              setCreateJobData(false);
            }}
            style={isRTL ? { marginLeft: "0", marginRight: "auto", order: -1 } : {}}
          ></button>
        </div>
        <div className="inter-font modal-body" style={{ direction: isRTL ? "rtl" : "ltr" }}>
          {["title", "company", "location"].map((field) => (
            <div className="inter-font mb-3" key={field} style={{ textAlign: isRTL ? "right" : "left" }}>
              <label className="inter-font form-label">
                {field === "title" && t("job_title")}
                {field === "company" && t("company")}
                {field === "location" && t("c_ff_text32")}
              </label>
              <input
                type="text"
                className="inter-font form-control"
                value={createForm?.[field]}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    [field]: e.target.value,
                  })
                }
                dir="ltr"
                style={{ textAlign: isRTL ? "right" : "left" }}
              />
            </div>
          ))}
        </div>
        <div className="inter-font modal-footer border-0" style={{ direction: isRTL ? "rtl" : "ltr" }}>
          <button
            className="inter-font btn btn-light"
            onClick={() => {
              setCreateJobData(false);
            }}
          >
            {t("new_text_30")} {/* Cancel */}
          </button>
          <button
            className="inter-font btn"
            style={{ backgroundColor: "#e15d4f", color: "#fff" }}
            onClick={handleCreateJob}
            disabled={isLoading}
          >
            {isLoading ? t("saving") : t("new_text_29")} {/* Saving... / Save Profile */}
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{/* Edit Job Modal */}
{editJobData && (
  <div
    className="inter-font modal fade show d-block"
    tabIndex="-1"
    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    dir={isRTL ? "rtl" : "ltr"}
  >
    <div className="inter-font modal-dialog modal-dialog-centered">
      <div
        className="inter-font modal-content border-0 shadow-lg rounded-4"
        style={{ backgroundColor: "#fff6f1" }}
      >
        <div className="inter-font modal-header border-0" style={{ direction: isRTL ? "rtl" : "ltr" }}>
          <h5 className="inter-font modal-title fw-bold" style={{ textAlign: isRTL ? "right" : "left", width: "100%" }}>
            {t("edit_job")}
          </h5>
          <button
            type="button"
            className="inter-font btn-close"
            onClick={() => {
              setEditJobData(null);
              setOpenMenuId(null);
            }}
            style={isRTL ? { marginLeft: "0", marginRight: "auto", order: -1 } : {}}
          ></button>
        </div>
        <div className="inter-font modal-body" style={{ direction: isRTL ? "rtl" : "ltr" }}>
          {["title", "company", "location"].map((field) => (
            <div className="inter-font mb-3" key={field} style={{ textAlign: isRTL ? "right" : "left" }}>
              <label className="inter-font form-label">
                {field === "title" && t("job_title")}
                {field === "company" && t("company")}
                {field === "location" && t("c_ff_text32")}
              </label>
              <input
                type="text"
                className="inter-font form-control"
                value={editForm?.[field]}
                onChange={(e) =>
                  setEditForm({ ...editForm, [field]: e.target.value })
                }
                dir="ltr"
                style={{ textAlign: isRTL ? "right" : "left" }}
              />
            </div>
          ))}
        </div>
        <div className="inter-font modal-footer border-0" style={{ direction: isRTL ? "rtl" : "ltr" }}>
          <button
            className="inter-font btn btn-light"
            onClick={() => {
              setEditJobData(null);
              setOpenMenuId(null);
            }}
          >
            {t("new_text_30")} 
          </button>
          <button
            className="inter-font btn"
            style={{ backgroundColor: "#e15d4f", color: "#fff" }}
            onClick={handleEditJob}
            disabled={isLoading}
          >
            {isLoading ? t("saving") : t("new_text_29")} 
          </button>
        </div>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default MyJob;
